import { useEffect, useState } from "react";
import { Button } from "../../@/components/ui/button";
import { MdModeEdit } from "react-icons/md";
import { AddUpdateCouponDto, useAddUpdateCouponMutation } from "../../api/api";
import { useToast } from "../../@/components/ui/use-toast";
import { Plus, X } from "lucide-react";
import SkeletonAddEditCouponsDialog from "../../skeletonLoading/SkeletonAddEditCouponsDialog";
import { MAX_CATEGORYNAME_LENGTH_MAX, USER_ROLE } from "../../lib/constants";

import moment from "moment";
import InfoTooltip from "../../components/common/InfoTooltip";
import GenericDropdown from "../../components/GenericDropDown";
import SortupDialog from "../../components/common/SortupDialog";
import { setNumberPrecision, useDecodedTokenResult } from "../../lib/utils";
type AddCouponDialogProps = {
  item?: any;
  isEditOnly: boolean;
  fetchCouponData: () => void;
  setSearchTerm: (searchTerm: string) => void;
  setPageNumber: (pageNumber: number) => void;
  fetchCouponTypes: () => Promise<any[]>;
  couponTypesData?: any[];
  vendorId: number | null;
};

interface Coupon {
  id: number;
  vendorId: number | null;
  code: string;
  maxUsageCount: number;
  expiryDate: string;
  couponType: string;
  couponValue: number;
  discountType: string;
  discountValue: number;
  statusId: number;
  description: string;
}

// Add this type for form field handling
type FormFieldType = keyof AddUpdateCouponDto;

export function AddEditCouponDialog({
  item,
  isEditOnly,
  fetchCouponData,
  setSearchTerm,
  setPageNumber,
  fetchCouponTypes,
  couponTypesData,
  vendorId,
}: AddCouponDialogProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [couponValues, setCouponValues] = useState<AddUpdateCouponDto>({
    vendorId: vendorId || null,
    code: "",
    maxUsageCount: 0,
    expiryDate: "",
    couponTypeId: 0,
    couponValue: 0,
    discountType: "PERCENTAGE",
    discountValue: 0,
    statusId: isEditOnly ? 0 : 18,
    description: "",
  });

  const toast = useToast();
  const { role } = useDecodedTokenResult();

  function validateExpiryDate(date: moment.MomentInput) {
    const selectedDate = moment(date);
    const now = moment();

    if (!selectedDate.isValid()) return "Invalid date";
    if (selectedDate.isBefore(now, "day")) {
      return "Expiry date must be today or in the future";
    }
    return ""; // No error
  }

  const [addCoupon, { isLoading: isCouponLoading }] =
    useAddUpdateCouponMutation();

  useEffect(() => {
    if (item) {
      setCouponValues({
        id: item.id,
        vendorId: vendorId,
        code: item.code,
        maxUsageCount: item.maxUsageCount,
        expiryDate: item.expiryDate
          ? new Date(item.expiryDate).toISOString().split("T")[0]
          : "",
        couponTypeId: Number(item.couponTypeId),
        couponValue: item.couponValue,
        discountType: item.discountType,
        discountValue: item.discountValue,
        statusId: item.statusId,
        description: item.description,
      });
    }
    if (isDialogOpen && (!localCouponTypes || localCouponTypes.length === 0)) {
      fetchCouponTypes();
    }
  }, [item, isDialogOpen, vendorId]);

  const [localCouponTypes, setLocalCouponTypes] = useState<any[]>([]);

  const handleDialogOpen = async () => {
    try {
      const response = await fetchCouponTypes();
      if (role === USER_ROLE.VENDOR) {
        const filteredResponse = response.filter((coupon) => coupon.id !== 2);
        setLocalCouponTypes(filteredResponse);
      } else {
        setLocalCouponTypes(response);
      }
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error fetching coupon types:", error);
    }
  };

  const [validationErrors, setValidationErrors] = useState({
    code: "",
    maxUsageCount: "",
    couponValue: "",
    discountValue: "",
    description: "",
    discountType: "",
  });

  const validateField = (field: string, value: any) => {
    switch (field) {
      case "code":
        return !value ? "Coupon code is required" : "";
      case "maxUsageCount":
        return value <= 0 ? "Usage count must be greater than 0" : "";
      case "couponValue":
        return value < 0
          ? "Coupon value must be greater than or equal to 0"
          : "";
      case "discountValue":
        if (couponValues.discountType === "PERCENTAGE") {
          return value <= 0 || value > 100
            ? "Percentage must be between 1 and 100"
            : "";
        } else if (couponValues.discountType === "FLAT") {
          return value <= 0 ? "Discount value must be greater than 0" : "";
        }
        return "";
      case "description":
        if (!value) return "Description is required";
        return value.length > 250
          ? "Description must be less than 250 characters"
          : "";
      default:
        return "";
    }
  };

  const handleCategoryNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, MAX_CATEGORYNAME_LENGTH_MAX);
    setCouponValues((prev) => ({ ...prev, code: value }));
    setValidationErrors((prev) => ({
      ...prev,
      code: validateField("code", value),
    }));
  };
  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setValidationErrors((prev) => ({
      ...prev,
      description: validateField("description", e.target.value),
    }));
    setCouponValues((prev) => ({ ...prev, description: e.target.value }));
  };

  const handleNumberInputChange =
    (field: FormFieldType) => (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;

      if (field === "discountValue") {
        value = value.replace(/[^0-9.]/g, "");
        value = setNumberPrecision(Number(value)).toString();
      } else {
        value = value.replace(/[^0-9]/g, "");
      }

      setCouponValues((prev) => ({
        ...prev,
        [field]:
          value === ""
            ? ""
            : field === "discountValue"
            ? parseFloat(value)
            : Number(value),
      }));

      setValidationErrors((prev) => ({
        ...prev,
        [field]: validateField(field, value),
      }));
    };

  const handleSelectChange = (field: FormFieldType) => (value: string) => {
    if (value.toString() === "-1") {
      value = "";
    }

    setCouponValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "discountType") {
      setValidationErrors((prev) => ({
        ...prev,
        discountValue: validateField(
          "discountValue",
          couponValues.discountValue
        ), // Re-validate discountValue
      }));
    }
  };

  const handleSelectNumberChange =
    (field: FormFieldType) => (value: string) => {
      setCouponValues((prev) => ({
        ...prev,
        [field]: Number(value), // Ensure conversion
      }));
    };

  const [isExpiryDateInvalid, setIsExpiryDateInvalid] = useState(false);

  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    const isFutureDate = moment(newDate).isAfter(moment());
    const validationMessage = validateExpiryDate(newDate);

    // Set the new date
    setCouponValues((prev) => ({ ...prev, expiryDate: newDate }));

    if (validationMessage) {
      // Display the error message for invalid date
      setIsExpiryDateInvalid(!isFutureDate);
      setCouponValues((prev) => ({ ...prev, expiryDate: "" })); // Clear invalid date
    } else {
      // Clear the error message when the date is valid
      setIsExpiryDateInvalid(false);
      setCouponValues((prev) => ({ ...prev, expiryDate: newDate })); // Set valid date
    }
  };

  const handleSubmitCoupon = async () => {
    // Validate all fields before submission
    const errors = {
      code: validateField("code", couponValues.code),
      maxUsageCount: validateField("maxUsageCount", couponValues.maxUsageCount),
      couponValue: validateField("couponValue", couponValues.couponValue),
      discountValue: validateField("discountValue", couponValues.discountValue),
      expiryDate: couponValues.expiryDate ? "" : "Expiry date is required",
      couponTypeId: couponValues.couponTypeId ? "" : "Coupon type is required",
      statusId: couponValues.statusId > 0 ? "" : "Coupon status is required",
      description: couponValues.description ? "" : "Description is required",
      discountType: couponValues.discountType
        ? ""
        : "Discount type is required",
    };

    setValidationErrors(errors);

    // Check if there are any validation errors
    if (Object.values(errors).some((error) => error !== "")) {
      // Find the first error and show it in the toast
      const firstError = Object.entries(errors).find(
        ([key, value]) => value !== ""
      );
      if (firstError) {
        toast.toast({
          title: "Validation Error!",
          description: firstError[1], // Show the specific error message
        });
      }
      return;
    }

    try {
      // const couponPayload = {
      //   ...couponValues,
      //   id: isEditOnly ? couponValues.id : undefined,
      //   expiryDate: new Date(couponValues.expiryDate).toISOString(),
      // };

      const couponPayload = isEditOnly
        ? { ...couponValues }
        : { ...couponValues, id: undefined };

      const response = await addCoupon({
        addUpdateCouponDto: couponPayload,
      }).unwrap();

      if (
        response?.message ===
        "Coupon code already exists, please try with different code."
      ) {
        handleCloseError(
          `Failed to ${
            isEditOnly ? "update" : "add"
          } coupon.Coupon code already exists, please try with different code.`
        );
      } else if (response?.message === "Coupon added/updated successfully") {
        await handleCloseSuccess(
          `Coupon ${isEditOnly ? "updated" : "added"} successfully.`
        );
      } else {
        handleCloseError(`Failed to ${isEditOnly ? "update" : "add"} coupon.`);
      }
    } catch (error) {
      const errorMessage = (error as any)?.data?.message; // For API errors (e.g., Axios)
      // Fallback message

      handleCloseError(
        `Error ${isEditOnly ? "updating" : "adding"} coupon: ${errorMessage}`
      );
    }
  };

  const resetCouponsValue = () => {
    setCouponValues({
      vendorId: vendorId,
      code: "",
      maxUsageCount: 0,
      expiryDate: "",
      couponTypeId: 0,
      couponValue: 0,
      discountType: "PERCENTAGE",
      discountValue: 0,
      statusId: isEditOnly ? 0 : 18,
      description: "",
    });
  };

  const handleCloseSuccess = async (message: string) => {
    setIsDialogOpen(false);
    resetCouponsValue();

    setPageNumber(1);
    await fetchCouponData();
    toast.toast({
      title: "Success!",
      description: message,
    });
  };

  const handleCloseError = (message: string) => {
    toast.toast({
      title: "Error!",
      description: message,
    });
  };

  const isFormDisabled =
    Object.values(validationErrors).some((error) => error !== "") ||
    couponValues.code === "" ||
    isCouponLoading ||
    !couponValues.maxUsageCount ||
    !couponValues.expiryDate ||
    !couponValues.couponTypeId ||
    couponValues.statusId <= 0 ||
    !couponValues.discountType;

  return (
    <>
      <div className="flex items-center space-x-2">
        {isEditOnly ? (
          <div onClick={handleDialogOpen}>
            <MdModeEdit
              size={16}
              className="cursor-pointer text-[#72767E] hover:text-black"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={handleDialogOpen}
            className="flex text-sm font-normal items-center justify-center bg-customGreenColor text-white px-4 py-2 gap-1 rounded-lg w-full text-nowrap"
          >
            <Plus className="w-4 h-4" />
            Add Coupon
          </button>
        )}
      </div>
      <SortupDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          resetCouponsValue();
          // Reset validation errors when dialog closes
          setValidationErrors({
            code: "",
            maxUsageCount: "",
            couponValue: "",
            discountValue: "",
            description: "",
            discountType: "",
          });
        }}
        header={isEditOnly ? "Edit Coupon" : "Add Coupon"}
        bodyClassName="w-full min-w-[37vw]"
        footerButtons={[
          {
            name: isEditOnly ? "Edit Coupon" : "Add Coupon",
            onClick: handleSubmitCoupon,
            className: isFormDisabled
              ? "opacity-50 cursor-not-allowed"
              : "text-white",
            disabled: isFormDisabled,
          },
          {
            name: "Cancel",
            className: "bg-white text-gray-900 border",
            disabled: isCouponLoading,
            onClick: () => {
              setIsDialogOpen(false);
              resetCouponsValue(); // Ensure this is called as a separate statement
            },
          },
        ]}
      >
        <div className="max-h-[32rem]">
          {isCouponLoading ? (
            <SkeletonAddEditCouponsDialog rows={5} />
          ) : (
            <>
              <div className="mt-1 pb-4">
                <label
                  htmlFor="code"
                  className="block text-sm font-medium text-gray-700"
                >
                  Coupon Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="code"
                  value={couponValues.code}
                  onChange={handleCategoryNameChange}
                  className={`focus:outline-customGreenColor mt-1 p-2 border rounded-md w-full ${
                    validationErrors.code ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {validationErrors.code && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.code}
                  </p>
                )}
                <label
                  htmlFor="maxUsageCount"
                  className="block text-sm font-medium text-gray-700 mt-4 flex items-center"
                >
                  Max Usage Count <span className="text-red-500 ml-1">*</span>
                  <InfoTooltip content="Set the maximum times this coupon can be redeemed by all users." />
                </label>
                <input
                  min="1"
                  type="number"
                  id="maxUsageCount"
                  value={couponValues.maxUsageCount}
                  onChange={handleNumberInputChange("maxUsageCount")}
                  className={`focus:outline-customGreenColor mt-1 p-2 border rounded-md w-full ${
                    validationErrors.maxUsageCount
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {validationErrors.maxUsageCount && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.maxUsageCount}
                  </p>
                )}
                <label
                  htmlFor="expiry"
                  className="block text-sm font-medium text-gray-700 mt-4"
                >
                  Expiry Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="expiryDate"
                  value={couponValues.expiryDate}
                  onChange={handleExpiryDateChange}
                  onFocus={(e) => e.target.showPicker()}
                  className={`focus:outline-none mt-1 p-2 border rounded-md w-full h-[38px] [&::-webkit-calendar-picker-indicator]:float-left [&::-webkit-datetime-edit]:text-left ${
                    isExpiryDateInvalid ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {isExpiryDateInvalid && (
                  <p className="text-red-500 text-sm mt-1">
                    Expiry date must be today or in the future.
                  </p>
                )}
                <label
                  htmlFor="couponType"
                  className="block text-sm font-medium text-gray-700 mt-4 mb-1"
                >
                  Coupon Type <span className="text-red-500">*</span>
                </label>
                <GenericDropdown
                  labelValue={
                    couponValues.couponTypeId
                      ? localCouponTypes.find(
                          (type) => type.id === couponValues.couponTypeId
                        )?.name
                      : ""
                  }
                  onChange={handleSelectChange("couponTypeId")}
                  options={localCouponTypes.map((type) => ({
                    value: type.id,
                    label: type.name,
                  }))}
                  placeholder="Select Coupon Type"
                  error={false}
                  isDisabled={isEditOnly}
                  className="w-full justify-between"
                  dropdownMenuContentWidth="w-[34vw]"
                  scrollHeight=""
                />
                <label
                  htmlFor="couponValue"
                  className="text-sm font-medium text-gray-700 mt-4 flex items-center"
                >
                  Coupon Redeemable Value{" "}
                  <span className="text-red-500 ml-1">*</span>
                  <InfoTooltip content="This coupon will be applied only if your total order amount exceeds the minimum required value specified here. Set the value to zero if you want the coupon to apply to all orders." />
                </label>
                <input
                  min="1"
                  type="number"
                  id="couponValue"
                  value={couponValues.couponValue}
                  onChange={handleNumberInputChange("couponValue")}
                  className={`focus:outline-customGreenColor mt-1 p-2 border border-gray-300 rounded-md w-full ${
                    validationErrors.couponValue ? "border-red-500" : ""
                  }`}
                />
                {validationErrors.couponValue && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.couponValue}
                  </p>
                )}
                <label
                  htmlFor="discountType"
                  className="block text-sm font-medium text-gray-700 mt-4 mb-1"
                >
                  Discount Type <span className="text-red-500">*</span>
                </label>
                <GenericDropdown
                  labelValue={
                    couponValues.discountType.toString() === "-1"
                      ? ""
                      : couponValues.discountType.toLowerCase()
                  }
                  onChange={handleSelectChange("discountType")}
                  options={[
                    { value: "PERCENTAGE", label: "percentage" },
                    { value: "FLAT", label: "flat" },
                  ]}
                  placeholder="Select Discount Type"
                  error={!!validationErrors.discountType}
                  className={`w-full justify-between ${
                    validationErrors.discountType
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  scrollHeight=""
                  dropdownMenuContentWidth="w-[34vw]"
                />
                {validationErrors.discountType && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.discountType}
                  </p>
                )}
                <label
                  htmlFor="discountValue"
                  className="block text-sm font-medium text-gray-700 mt-4 flex items-center"
                >
                  Max Discount <span className="text-red-500 ml-1">*</span>
                  <InfoTooltip content="Specify the discount amount or percentage that this coupon provides." />
                </label>
                <input
                  min="1"
                  type="number"
                  id="discountValue"
                  value={couponValues.discountValue}
                  onChange={handleNumberInputChange("discountValue")}
                  className={`focus:outline-customGreenColor mt-1 p-2 border border-gray-300 rounded-md w-full ${
                    validationErrors.discountValue ? "border-red-500" : ""
                  }`}
                />
                {validationErrors.discountValue && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.discountValue}
                  </p>
                )}
                <label
                  htmlFor="discountValue"
                  className="text-sm font-medium text-gray-700 mt-4 flex items-center"
                >
                  Description <span className="text-red-500 ml-1">*</span>
                  <InfoTooltip content="Add a description to this coupon to help customer with some more info." />
                </label>
                <textarea
                  id="description"
                  value={couponValues.description}
                  onChange={handleDescriptionChange}
                  className={`focus:outline-customGreenColor mt-1 p-2 border border-gray-300 rounded-md w-full resize-none ${
                    validationErrors.description ? "border-red-500" : ""
                  }`}
                />
                {validationErrors.description && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.description}
                  </p>
                )}
                <label
                  htmlFor="statusId"
                  className="block text-sm font-medium text-gray-700 mt-4 mb-1"
                >
                  Status <span className="text-red-500">*</span>
                </label>
                <GenericDropdown
                  labelValue={
                    couponValues.statusId.toString() === "-1"
                      ? "" // This will show what's being returned
                      : Number(couponValues.statusId) === 18
                      ? "Active"
                      : "Inactive"
                  }
                  onChange={handleSelectNumberChange("statusId")}
                  options={[
                    { value: "18", label: "Active" },
                    { value: "19", label: "Inactive" },
                  ]}
                  placeholder="Select Status"
                  error={false}
                  className="w-full justify-between"
                  scrollHeight=""
                  dropdownMenuContentWidth="w-[34vw]"
                />
              </div>
            </>
          )}
        </div>
      </SortupDialog>
    </>
  );
}
